import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useCurrentWorkspace, useUpdateCurrentWorkspace } from '@/api/workspaces';
import type { Workspace } from '@/api/types';
import { Badge, Button, Card, ErrorState, Input, Spinner, useToast } from '@/components/ui';
import { useAuth } from '@/app/auth/useAuth';
import { subject } from '@/app/auth/ability';
import { formatRelative, humanize } from '@/lib/format';
import { PLAN_TONE } from '@/features/platform/meta';

const schema = z.object({
  name: z.string().trim().min(2, 'At least 2 characters').max(120),
  contactName: z.string().trim().max(120),
  contactEmail: z.string().trim().email('Enter a valid email'),
  website: z.string().trim().url('Enter a full URL').or(z.literal('')),
});
type FormValues = z.infer<typeof schema>;

/** Settings → Workspace: the client's profile. Only the owner (or a super admin) can edit it. */
export function WorkspacePage() {
  const ws = useCurrentWorkspace();
  if (ws.isPending)
    return (
      <div className="flex justify-center py-16">
        <Spinner size="lg" label="Loading workspace" />
      </div>
    );
  if (ws.error) return <ErrorState error={ws.error} onRetry={() => void ws.refetch()} />;
  return <WorkspaceForm workspace={ws.data} />;
}

function WorkspaceForm({ workspace }: { workspace: Workspace }) {
  const { ability } = useAuth();
  const toast = useToast();
  const update = useUpdateCurrentWorkspace();
  const canEdit = ability.can('update', subject('Workspace', { id: workspace.id }));
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: workspace.name,
      contactName: workspace.contactName,
      contactEmail: workspace.contactEmail,
      website: workspace.website ?? '',
    },
  });

  const onSubmit = handleSubmit((v) =>
    update.mutate(
      { ...v, website: v.website || null },
      {
        onSuccess: (saved) => {
          reset({ ...v });
          toast.success(`${saved.name} saved`);
        },
        onError: (e) => toast.error(e.message),
      },
    ),
  );

  return (
    <Card className="p-5 sm:p-6">
      <div className="mb-5 flex flex-wrap items-center gap-2">
        <h2 className="font-heading text-lg font-bold text-slate-900">{workspace.name}</h2>
        <Badge tone={PLAN_TONE[workspace.plan]}>{humanize(workspace.plan)} plan</Badge>
        <span className="text-xs text-slate-500">
          <span className="font-mono">{workspace.slug}</span> · created {formatRelative(workspace.createdAt)}
        </span>
      </div>
      {!canEdit && (
        <p className="mb-4 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
          Only the workspace owner can change these details.
        </p>
      )}
      <form onSubmit={onSubmit} noValidate>
        <fieldset disabled={!canEdit} className="grid gap-4 sm:grid-cols-2">
          <Input label="Company name" required error={errors.name?.message} {...register('name')} />
          <Input label="Website" error={errors.website?.message} {...register('website')} />
          <Input
            label="Billing / contact name"
            error={errors.contactName?.message}
            {...register('contactName')}
          />
          <Input
            label="Billing / contact email"
            type="email"
            error={errors.contactEmail?.message}
            {...register('contactEmail')}
          />
        </fieldset>
        {canEdit && (
          <div className="mt-5 flex justify-end">
            <Button type="submit" loading={update.isPending} disabled={!isDirty}>
              Save workspace
            </Button>
          </div>
        )}
      </form>
      <p className="mt-6 text-xs text-slate-500">
        Plan changes and suspension are handled by Kollabr — contact your account manager.
      </p>
    </Card>
  );
}
